<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div class="not-the-same-wrapper">
          <div class="steps-container">
            <div class="step step-0">
              <img class="substep" id="image-0" :src="imageLeftSrc" alt="" />
              <div
                :key="stepIndex"
                v-for="(step, stepIndex) in stepsLeft"
                v-show="stepIndex <= progress.stepIndex"
              >
                <blinking-cursor-text
                  :id="`step-${stepIndex}`"
                  :key="stepIndex"
                  :text="step.answer"
                  :text-step="stepIndex"
                  :class-name="'type-line title'"
                >
                </blinking-cursor-text>
              </div>
            </div>

            <div class="step step-1">
              <img class="substep" id="image-1" :src="imageRightSrc" alt="" />
              <div
                :key="stepIndex"
                v-for="(step, stepIndex) in stepsRight"
                v-show="stepsLeft.length + stepIndex <= progress.stepIndex"
              >
                <blinking-cursor-text
                  :id="`step-${stepsLeft.length + stepIndex}`"
                  :key="stepsLeft.length + stepIndex"
                  :text="step.answer"
                  :text-step="stepsLeft.length + stepIndex"
                  :class-name="'type-line title'"
                >
                </blinking-cursor-text>
              </div>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    BlinkingCursorText
  },

  data() {
    return {};
  },
  computed: {
    steps() {
      return this.currentRound?.steps ?? [];
    },
    stepsLeft() {
      return this.currentRound?.steps?.filter(({ isLeft }) => isLeft) ?? [];
    },
    stepsRight() {
      return this.currentRound?.steps?.filter(({ isLeft }) => !isLeft) ?? [];
    },
    imageLeftSrc() {
      const [firstLeftStep] = this.stepsLeft;
      return firstLeftStep?.notTheSameOptionalImage?.image?.src;
    },
    imageRightSrc() {
      const [firstRightStep] = this.stepsRight;
      return firstRightStep?.notTheSameOptionalImage?.image?.src;
    }
  },
  methods: {
    async loadActivity() {
      this.createRounds();
      await this.loadImages();
    },

    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.not-the-same-wrapper');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 45}px)`;
    },

    async loadImages() {
      const { rounds } = this.activityData;
      rounds.forEach(({ steps }) => {
        steps.forEach(({ notTheSameOptionalImage }) => {
          this.queueImage(notTheSameOptionalImage?.image?.src);
        });
      });

      this.downloadImages();
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.setContainerHeight();
        TweenMax.to(['#step-0', '#image-0'], 1, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        const { stepIndex } = this.progress;
        const stepsTotal = this.currentRound.steps.length;

        if (this.isEndOfActivity && stepIndex === stepsTotal) {
          // end of activity
          this.playCongrats();
          // play congrats first, then end the activity after delay
          TweenMax.delayedCall(1.5, () => this.completeActivity(false));
          return;
        }

        if (stepIndex >= this.stepsLeft?.length) {
          // show image
          TweenMax.to('#image-1', 1, { opacity: 1 });
        }

        // show paragraph to be typed
        TweenMax.to(`#step-${stepIndex}`, 1, { opacity: 1 });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.substep,
.type-line {
  opacity: 0;
}

.not-the-same-wrapper {
  padding-left: 50px;
  padding-right: 50px;

  .steps-container {
    height: 100%;

    .step {
      display: inline-block;
      height: 100%;
      width: 40%;

      img {
        height: 30%;
        max-height: 100px;
      }

      &:last-child {
        float: right;
      }

      .type-line {
        font-size: 20px;

        &.title {
          margin-bottom: 5px;
        }
      }
    }
  }
}

.is-chrome-ipad {
  .steps-container {
    img {
      max-height: 180px;
      margin-top: 15px;
    }
  }
}
</style>
